/* ──────────────────────────────────────────────────────────────────────
   /mall — Faitharama Market Mall  (v2 — banner scroller + grid)
   ──────────────────────────────────────────────────────────────────────
   Mike's directive Feb 2026:
   1. Top section: AUTO-SCROLLING banner carousel showing each partner's
      best/hero banner (one big banner at a time, rotates every 5s).
   2. Below: GRID of all partner banner tiles — visitors see the same
      brand in both places and can click whichever they recognize.
   3. AMAZON-STYLE attribution: any visitor who lands on /mall?aff=mike
      gets that affiliate ID stored in localStorage for 60 days. Every
      single door click from then on carries ?aff=mike through, so the
      affiliate gets credit no matter which store the visitor buys from.
   ────────────────────────────────────────────────────────────────────── */
import { useEffect, useState, useMemo } from "react";
import { Link, useLocation } from "react-router-dom";
import SiteNav from "../components/SiteNav";
import SiteFooter from "../components/SiteFooter";
import { ChevronLeft, ChevronRight, Heart } from "lucide-react";
import { useAuth } from "../context/AuthContext";

const G    = "#2E9F3A";
const O    = "#F39A1F";
const RED  = "#C9202E";
const GOLD = "#D4A017";
const INK  = "#0F172A";

/* ───── PARTNERS — Edit this single array to add/remove stores. ─────
   bannerBg / bannerImg are the brand's "best banner".
   - bannerImg (optional): full hero banner URL (preferred)
   - bannerBg (fallback): gradient when no image yet
*/
/* Banner image URLs. Customer assets for brand-supplied ads; /api/uploads for
   AI-generated image ads. Every tile must have bannerImg — NO colored squares. */
/* All banner images use customer-asset CDN URLs so they survive every redeploy. */
const UP_NOURISH_AD       = "https://customer-assets.emergentagent.com/job_grace-community-plus/artifacts/xzkog6h6_UPNOURISH%20AD.png";
const FAITHARAMA_BOOK_AD  = "https://customer-assets.emergentagent.com/job_grace-community-plus/artifacts/qe4t13t2_WITH%20FAITH%20ALL%20THINGS%20ARE%20POSSIBLE%20BOOK%2C%20COURSE%2C%20AND%20AUDIO.png";
const ACADEMY_AD          = "https://customer-assets.emergentagent.com/job_grace-community-plus/artifacts/50opfc2b_THE%20COMPLETE%20FAITHARAMA.COM%20TOOLKIT%20GUIDE.png";
const AFFILIATE_101_AD    = "https://customer-assets.emergentagent.com/job_grace-community-plus/artifacts/7mqgj7qx_1000%20dollars%20in%2090%20days.png";
const GARDEN_AD           = "https://customer-assets.emergentagent.com/job_grace-community-plus/artifacts/v007eedq_Screenshot%202026-06-08%20221226.png";
const GOLI_AD             = "https://customer-assets.emergentagent.com/job_grace-community-plus/artifacts/4jq13qjg_goli.com%20gummies.png";
const BEE_AND_YOU_AD      = "https://customer-assets.emergentagent.com/job_grace-community-plus/artifacts/o08i64t2_Screenshot%202026-06-06%20125500.png";
const GOGREEN_HEMP_AD     = "https://customer-assets.emergentagent.com/job_grace-community-plus/artifacts/ebulo6f5_Screenshot%202026-06-09%20171454.png";
const PIURIFY_AD          = "https://customer-assets.emergentagent.com/job_grace-community-plus/artifacts/xg6blyqf_Screenshot%202026-05-13%20134456.png";
const BEACHRC_AD          = "https://customer-assets.emergentagent.com/job_grace-community-plus/artifacts/jqkt2k8o_Screenshot%202026-06-09%20105917.png";
const CHRISTIAN_CAFE_AD   = "https://customer-assets.emergentagent.com/job_grace-community-plus/artifacts/4pj7t8vl_Screenshot%202026-06-14%20215258.png";
const DETOX_ORGANICS_AD   = "https://customer-assets.emergentagent.com/job_grace-community-plus/artifacts/2abfkak8_DETOX%20ORGANICS.png";
const ERA_ORGANICS_AD     = "https://customer-assets.emergentagent.com/job_grace-community-plus/artifacts/0h4sdtai_ERA%20ORGANIC%20MOMS%20AND%20KIDS.png";

/* 5-Minute Garden affiliate link. */
const GARDEN_HREF = "http://www.betterdailyguide.site/ds24/5-minute-garden#aff=mylivegoodplan2b9f";

const STORES = [
  { name: "5-Minute Garden",       sells: "Grow A Full Year's Food In Less Than 100 Sq Ft",  href: GARDEN_HREF, external: true, bannerImg: GARDEN_AD, accent: G, featured: true,   memberPct: 37.5 },
  { name: "UpNourish",             sells: "Plant Protein · Creatine · Magnesium · GLP-1",      href: "https://upnourishbrand.com/michael345",  external: true, bannerImg: UP_NOURISH_AD, accent: "#C6F73F", featured: true, memberPct: 5    },
  { name: "WellFit Treadmills",    sells: "TM029 Pro · 500 lb Capacity · Built For Seniors",  href: "https://wellfittreadmill.com/?ref=MICHAELBOYD", external: true, bannerImg: "https://customer-assets.emergentagent.com/job_grace-community-plus/artifacts/jgtmpxxd_Screenshot%202026-06-09%20215401.png", accent: "#1E64C4", featured: true, memberPct: 2.5 },
  { name: "Faitharama Books",      sells: "Books · Audio · Devotionals · Faith Stories",      href: "/with-faith-all-things-are-possible-book", bannerImg: FAITHARAMA_BOOK_AD, accent: RED, memberPct: 50 },
  { name: "Faitharama Academy",    sells: "Online Courses · Plain-English Training",          href: "/academy",         bannerImg: ACADEMY_AD,                  accent: G,   memberPct: 50 },
  { name: "Affiliate Marketing 101",sells: "Beginner Playbook · 90-Day Plan",                 href: "/business/affiliate-marketing-101", bannerImg: AFFILIATE_101_AD, accent: O, memberPct: 50 },
  { name: "ChristianCafe.com",     sells: "Christian Singles Since 1999 · 10-Day Free Trial",  href: "https://yazing.com/deals/christiancafe/faitharama", external: true, bannerImg: CHRISTIAN_CAFE_AD, accent: "#5C2E0F", featured: true,  memberPct: 40 },
  { name: "Detox Organics",        sells: "Organic Superfoods · Chocolate · Daily Boost",      href: "https://yazing.com/deals/detoxorganics/faitharama",  external: true, bannerImg: DETOX_ORGANICS_AD, accent: "#3A5F1F", memberPct: 15 },
  { name: "Era Organics",          sells: "Clean Beauty · Mom & Kids · Diaper Balm · Superbalm", href: "https://yazing.com/deals/eraorganics/faitharama",   external: true, bannerImg: ERA_ORGANICS_AD, accent: "#5DB6C9", memberPct: 10 },
  { name: "Goli",                  sells: "Apple Cider Vinegar Gummies · Sleep Gummies",       href: "https://go.goli.com/g-482b22a7f8", external: true, bannerImg: GOLI_AD, accent: "#E94B5C", memberPct: 7.5 },
  { name: "Bee & You",             sells: "Raw Honey · Royal Jelly · Propolis · Immunity",    href: "https://www.beeandyou.com/?rfsn=9150485.60554a&utm_source=refersion&utm_medium=affiliate&utm_campaign=9150485.60554a", external: true, bannerImg: BEE_AND_YOU_AD, accent: O, memberPct: 10 },
  { name: "GoGreen Hemp",          sells: "Broad-Spectrum CBD · 250 / 500 / 1000 mg Tinctures", href: "https://www.awin1.com/cread.php?awinmid=88879&awinaffid=2924149", external: true, bannerImg: GOGREEN_HEMP_AD, accent: "#3FB04A", memberPct: 7.5 },
  { name: "Piurify",               sells: "Clean Water Filtration · Hydrogen Generators",     href: "/piurify",         bannerImg: PIURIFY_AD, accent: "#1E64C4", memberPct: 5 },
  { name: "Free Stock Photos",     sells: "Royalty-Free Library · Save monthly",               href: "/dashboard/photos", accent: "#7B3FA3" },
  { name: "Member Directory",      sells: "Find A Faitharama Affiliate Near You",             href: "/directory",        accent: G },
  { name: "BeachRC.com",           sells: "RC Race Buggies · Sworkz · Hobby Grade",            href: "https://www.beachrc.com?aff=1841",  external: true, bannerImg: BEACHRC_AD, accent: RED, memberPct: 12.5 },
  { name: "Submit Your Product",   sells: "Vendors · Apply To Join The Mall",                 href: "/vendor-standards", accent: G },
];

const FEATURED = STORES.filter((s) => s.featured).slice(0, 5);
const STORAGE_KEY = "faitharama_aff_ref";
const AFF_DAYS    = 60;

export default function MallMarketPage() {
  const loc = useLocation();
  const { user } = useAuth();
  const [carouselIdx, setCarouselIdx] = useState(0);
  const [affRef, setAffRef]           = useState(null);

  /* ── AMAZON-STYLE attribution: capture & persist any inbound ?aff=X ── */
  useEffect(() => {
    const params = new URLSearchParams(loc.search);
    const inbound = params.get("aff");
    if (inbound) {
      const payload = { aff: inbound, ts: Date.now() };
      try { localStorage.setItem(STORAGE_KEY, JSON.stringify(payload)); } catch {}
      setAffRef(inbound);
    } else {
      try {
        const raw = localStorage.getItem(STORAGE_KEY);
        if (raw) {
          const { aff, ts } = JSON.parse(raw);
          const ageDays = (Date.now() - ts) / 86400000;
          if (aff && ageDays < AFF_DAYS) setAffRef(aff);
        }
      } catch {}
    }
  }, [loc.search]);

  /* Append affiliate ref to every door link.
     For EXTERNAL partner URLs, also tag the outbound click with
     fa_ref so we can match the payout back to the right sub-affiliate. */
  const withRef = useMemo(() => (path, external = false) => {
    if (!affRef) return path;
    if (external) {
      // External: append fa_ref so we know which Faitharama sub-affiliate
      // sent this visitor when the partner pays us a commission.
      return `${path}${path.includes("?") ? "&" : "?"}fa_ref=${affRef}`;
    }
    return `${path}${path.includes("?") ? "&" : "?"}aff=${affRef}`;
  }, [affRef]);

  /* Auto-advance the featured banner carousel every 5 seconds. */
  useEffect(() => {
    const id = setInterval(() => {
      setCarouselIdx((i) => (i + 1) % FEATURED.length);
    }, 5000);
    return () => clearInterval(id);
  }, []);

  const featured = FEATURED[carouselIdx];

  useEffect(() => { document.title = "The Faitharama Market — Walk The Mall"; }, []);

  return (
    <div className="min-h-screen bg-white" data-testid="mall-market-page">
      <SiteNav />

      {/* ═══ HERO BANNER STRIP ═══ */}
      <section className="border-b-2" style={{ background: "#E0F2FE", borderColor: G }}>
        <div className="max-w-6xl mx-auto px-5 md:px-8 pt-8 pb-4">
          <div className="text-center mb-4">
            <div className="text-[10px] tracking-[0.32em] uppercase font-bold mb-2" style={{ color: RED }}>
              ★ WELCOME TO THE FAITHARAMA MARKET
            </div>
            <h1 className="font-serif-display uppercase leading-[0.95]"
                style={{ color: INK, fontSize: "clamp(1.85rem, 4vw, 3rem)" }}>
              Walk The <span style={{ color: G }}>Mall</span>. <span style={{ color: O }}>Pick A Door.</span>
            </h1>
            {affRef && (
              <div className="mt-3 inline-flex items-center gap-2 px-4 py-1.5 rounded-full text-sm font-bold text-white shadow"
                   style={{ background: G }} data-testid="aff-credit-strip">
                ✓ Welcome — your visit is credited to <strong className="underline">@{affRef}</strong>
              </div>
            )}
          </div>

          {/* ═══ AUTO-SCROLLING FEATURED BANNER CAROUSEL ═══
              Fixed 16:9 slot capped at a reasonable height so banners
              never blow up to fill the screen. Tall logos get letter-boxed
              against the white container — no more giant takeover.       */}
          <div className="relative rounded-2xl overflow-hidden shadow-2xl bg-white mx-auto"
               data-testid="mall-banner-carousel"
               style={{ maxWidth: "1100px" }}>
            <SmartLink to={withRef(featured.href, featured.external)} external={featured.external}
                  data-testid={`featured-banner-${featured.name.toLowerCase().replace(/[^a-z0-9]+/g,"-")}`}
                  className="block group relative"
                  key={featured.name}>
              <div className="relative w-full bg-white"
                   style={{ aspectRatio: "16 / 9", maxHeight: "420px" }}>
                <img src={featured.bannerImg} alt={featured.name}
                     className="absolute inset-0 w-full h-full"
                     style={{ objectFit: "contain" }} />
              </div>

              {/* Featured ribbon */}
              <div className="absolute top-3 left-3 z-10 flex gap-2">
                <span className="px-2 py-1 rounded-full text-[10px] font-bold shadow"
                      style={{ background: GOLD, color: INK }}>★ FEATURED PARTNER</span>
              </div>
              {featured.external && (
                <span className="absolute top-3 right-14 z-10 px-2 py-1 rounded-full text-[10px] font-bold shadow"
                      style={{ background: "rgba(255,255,255,0.92)", color: INK }}>↗ EXTERNAL</span>
              )}
            </SmartLink>

            {/* Carousel controls */}
            <button
              onClick={(e) => { e.preventDefault(); setCarouselIdx((i) => (i - 1 + FEATURED.length) % FEATURED.length); }}
              className="absolute left-3 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-black/35 hover:bg-black/55 text-white flex items-center justify-center transition"
              aria-label="Previous banner"
              data-testid="carousel-prev">
              <ChevronLeft className="w-5 h-5" />
            </button>
            <button
              onClick={(e) => { e.preventDefault(); setCarouselIdx((i) => (i + 1) % FEATURED.length); }}
              className="absolute right-3 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-black/35 hover:bg-black/55 text-white flex items-center justify-center transition"
              aria-label="Next banner"
              data-testid="carousel-next">
              <ChevronRight className="w-5 h-5" />
            </button>

            {/* Carousel dots */}
            <div className="absolute bottom-3 left-1/2 -translate-x-1/2 flex gap-2">
              {FEATURED.map((_, i) => (
                <button
                  key={i}
                  onClick={(e) => { e.preventDefault(); setCarouselIdx(i); }}
                  className="w-2.5 h-2.5 rounded-full transition"
                  style={{ background: i === carouselIdx ? "#fff" : "rgba(255,255,255,0.35)" }}
                  aria-label={`Go to slide ${i + 1}`}
                />
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ═══ FULL BANNER GRID — every partner, no scroll ═══ */}
      <section className="py-10 md:py-14 bg-white">
        <div className="max-w-6xl mx-auto px-5 md:px-8">
          <div className="text-center mb-7">
            <div className="text-[10px] tracking-[0.32em] uppercase font-bold mb-1" style={{ color: RED }}>
              ★ ALL {STORES.length} STORES
            </div>
            <h2 className="font-serif-display uppercase leading-[0.98]"
                style={{ color: INK, fontSize: "clamp(1.65rem, 3.5vw, 2.5rem)" }}>
              Click <span style={{ color: G }}>Any Banner</span> To Enter.
            </h2>
            {affRef && (
              <p className="text-sm md:text-base mt-2 italic" style={{ color: INK, opacity: 0.8 }}>
                No matter which store you buy from, <strong>@{affRef}</strong> gets the credit.
              </p>
            )}
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-5"
               data-testid="mall-store-grid">
            {STORES.map((s) => (
              <SmartLink
                key={s.name}
                to={withRef(s.href, s.external)}
                external={s.external}
                data-testid={`mall-tile-${s.name.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                className="group relative block rounded-xl overflow-hidden shadow-md hover:shadow-2xl hover:-translate-y-1 transition-all bg-white"
                style={{ aspectRatio: "16 / 9" }}
              >
                {s.bannerImg ? (
                  <img src={s.bannerImg} alt={s.name} loading="lazy"
                       className="absolute inset-0 w-full h-full object-contain" />
                ) : (
                  /* Fallback tile — solid brand color + name + tagline */
                  <div className="absolute inset-0 flex flex-col items-center justify-center text-white p-4 text-center"
                       style={{ background: `linear-gradient(135deg, ${s.accent} 0%, ${INK} 130%)` }}>
                    <div className="font-serif-display uppercase leading-tight"
                         style={{ fontSize: "clamp(1.1rem, 2vw, 1.7rem)", textShadow: "0 2px 8px rgba(0,0,0,0.45)" }}>
                      {s.name}
                    </div>
                    <div className="mt-2 text-xs md:text-sm opacity-95"
                         style={{ textShadow: "0 1px 6px rgba(0,0,0,0.5)" }}>
                      {s.sells}
                    </div>
                  </div>
                )}
                {/* Featured ribbon */}
                {s.featured && (
                  <div className="absolute top-2 left-2 z-10 px-2 py-1 rounded-full text-[9px] font-bold shadow"
                       style={{ background: GOLD, color: INK }}>
                    ★ FEATURED
                  </div>
                )}
                {/* External-store badge */}
                {s.external && (
                  <div className="absolute bottom-2 right-2 z-10 px-2 py-1 rounded-full text-[9px] font-bold shadow"
                       style={{ background: "rgba(255,255,255,0.92)", color: INK }}>
                    ↗ EXTERNAL
                  </div>
                )}
                {/* ★ MEMBERS-ONLY · Your payout per sale (50% of Mike's commission) */}
                {user && s.memberPct != null && (
                  <div
                    data-testid={`payout-${s.name.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                    className="absolute bottom-2 left-2 z-10 px-3 py-1.5 rounded-full text-[10px] font-bold shadow-lg uppercase tracking-wider"
                    style={{ background: G, color: "#fff", border: "2px solid #fff" }}
                  >
                    YOU EARN {s.memberPct}% / SALE
                  </div>
                )}
                {/* Hover overlay shine */}
                <div className="absolute inset-0 bg-white/0 group-hover:bg-white/10 transition" />
              </SmartLink>
            ))}
          </div>

          <p className="mt-8 text-center text-base md:text-lg" style={{ color: INK }}>
            Want your brand in the mall? <Link to="/vendor-standards" className="font-bold underline" style={{ color: G }}>Submit your product →</Link>
          </p>
        </div>
      </section>

      <SiteFooter />

      <style>{`
        @keyframes bannerTextIn {
          0%   { opacity: 0; transform: translateY(14px); filter: blur(6px); }
          60%  { opacity: 1; transform: translateY(0);    filter: blur(0); }
          100% { opacity: 1; transform: translateY(0); }
        }
        .banner-text-enter { animation: bannerTextIn 0.7s cubic-bezier(0.22, 1, 0.36, 1) both; }
      `}</style>
    </div>
  );
}

/* SmartLink — uses <Link> for internal routes and <a target="_blank"> for external. */
function SmartLink({ to, external, children, ...rest }) {
  if (external) {
    return (
      <a href={to} target="_blank" rel="noopener noreferrer" {...rest}>
        {children}
      </a>
    );
  }
  return <Link to={to} {...rest}>{children}</Link>;
}
